import React, { useMemo } from 'react';
import { map, get } from 'lodash';
import classnames from 'classnames';

// local dependencies
import {
	MultiStepManager,
	StepsNavigation,
	StepList,
	Step,
} from 'modules/multi-step-manager';
import { getLabel } from 'utils/getLabel';
import { getStepView } from 'resolvers/widgetsStepFlow';
import { Icon } from 'modules/icon-bank';
import { widgetPreloader as WidgetPreloader } from 'ui/views/generic/widgetPreloader';

/*******************************************/

// step navigation view
const widgetsStepFlowNavigation = ( props ) => {
	return (
		<div className="ui-widgets-step-flow__navigation">
			{map( props.steps, ( step, index ) => {

				// step status
				const isStepCompleted = index < props.activeStepIndex;
				const isStepActive = index === props.activeStepIndex;

				// step item class name
				const itemClassName = classnames(
					'ui-widgets-step-flow__navigation__item',
					{
						'ui-widgets-step-flow__navigation__item--completed': isStepCompleted,
						'ui-widgets-step-flow__navigation__item--active': isStepActive,
					}
				);

				return (
					<div key={step.name} className={itemClassName}>
						{/* index */}
						<div className="ui-widgets-step-flow__navigation__item__number">
							{isStepCompleted ? (
								<div className="ui-widgets-step-flow__navigation__item__number__icon">
									<Icon name="checkmark" fill="#ffffff" size="10" height="10" />
								</div>
							) : (
								<div className="ui-widgets-step-flow__navigation__item__number__text">
									{index + 1}
								</div>
							)}
						</div>

						{/* title */}
						<div className="ui-widgets-step-flow__navigation__item__title">
							{getLabel( step.title )}
						</div>
					</div>
				);
			} )}
		</div>
	);
};

// set display name
widgetsStepFlowNavigation.displayName =
	'widgetsStepFlow.widgetsStepFlowStepWrapper';

/*******************************************/

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { widgetsStepFlow as WidgetsStepFlowView } from 'modules/widgetsStepFlow';
 *
 * // render
 * <WidgetsStepFlowView propName='propValue'/>
 */
export const widgetsStepFlow = ( props ) => {

    // create context for multi-step-manager
    const context = useMemo( React.createContext, [] );

    // store steps as static data
    const steps = useMemo( () => map( props.steps, step => ( {
        ...step,
        view: getStepView( step.name ), // resolve and store widget for the step
    } ) ), [] );

	//check if hideNavigationSteps property exists
	const hideNavigationSteps = get( props, 'hideNavigationSteps', false );

    return (
        <div className='ui-widgets-step-flow'>
            <MultiStepManager context={ context } activeStepIndex={ props.activeStepIndex } subscribeActiveStepIndex={ false } totalSteps={ steps.length }>

                {/* navigation */}
                { !hideNavigationSteps && (
					<StepsNavigation
						context={context}
						steps={steps}
						view={widgetsStepFlowNavigation}
					/>
				) }

                {/* steps container */}
                <div className='ui-widgets-step-flow__steps-container'>
                    <StepList context={ context }>
                        {
                            steps.map( ( step ) => {
                                return (
                                    <Step
                                        context={ context }
                                        key={ step.name }
                                        view={ step.view }
                                        requestPayload={ props.requestPayload }
                                        widget={ step }
                                    />
                                );
                            } )
                        }
                    </StepList>
                </div>
            </MultiStepManager>
        </div>
    );
};

// set display name
widgetsStepFlow.displayName = 'widgetsStepFlow';

// set default props
widgetsStepFlow.defaultProps = {
	activeStepIndex: 0,
	steps: [],
    requestPayload: {},
};

// prop types
widgetsStepFlow.propTypes = {};

/*******************************************/

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { widgetsStepFlowPreloader as WidgetsStepFlowPreloader } from 'modules/widgetsStepFlow';
 *
 * // render
 * <WidgetsStepFlowPreloader propName='propValue'/>
 */
export const widgetsStepFlowPreloader = () => {
	return (
		<div className="ui-widgets-step-flow-preloader">
			<div className="ui-widgets-step-flow-preloader__navigation">
				{map( [ 1, 2 ], ( item ) => {
					return (
						<div
							key={item}
							className="ui-widgets-step-flow-preloader__navigation__item"
						>
							<div className="ui-widgets-step-flow-preloader__navigation__item__number" />
							<div className="ui-widgets-step-flow-preloader__navigation__item__text" />
						</div>
					);
				} )}
			</div>
			<div className="ui-widgets-step-flow-preloader__steps">
				<WidgetPreloader />
			</div>
		</div>
	);
};

// set display name
widgetsStepFlowPreloader.displayName = 'widgetsStepFlowPreloader';

// set default props
widgetsStepFlowPreloader.defaultProps = {
	activeStepIndex: 0,
	steps: [],
};

// prop types
widgetsStepFlowPreloader.propTypes = {};
